import React, { useState } from 'react'
import { CheckBoxInput } from '../../../../components/form-control/CheckBoxInput'
import Button from '../../../../components/form-control/Button'
import { agreeToTermsAndConditions } from '../../../../api/requests'
import usePartner from '../../../../hooks/partnerHook';
import { useDispatch, useSelector } from 'react-redux';
import { getCompanyOnboardingStatusFromState, SET_COMPANY_ONBOARDING_STATUS, SET_PENDING_ACTIONS } from '../../../../states/slices/globalSlice';
import { BadgeCheck, CheckCheckIcon } from 'lucide-react';
import { BsCalendar, BsPerson } from 'react-icons/bs';
import { getFormattedDate } from '../../../../utils/miscFunctions';
import useUserAccount from '../../../../hooks/userAccountHook';

type ITermsAndConditions = {
    hideControls?: boolean
    onSuccess?: () => void
}

const TermsAndConditions = ({ hideControls = true, onSuccess }: ITermsAndConditions) => {
    const onboardingStatus = useSelector(getCompanyOnboardingStatusFromState);
    const [acceptTerms, setAcceptTerms] = useState<boolean | null>(onboardingStatus?.companyInfo?.termsAndConditionsAccepted)
    const [loading, setLoading] = useState<boolean>(false)
    const { fetchCompanyOnboardingStatus } = usePartner();
    const dispatch = useDispatch();
    const { user } = useUserAccount();

    const getCompanyOnboardingStatus = async () => {

        try {
            const data = await fetchCompanyOnboardingStatus();
            if (data) {
                dispatch(SET_PENDING_ACTIONS(data?.pendingActions));
                dispatch(SET_COMPANY_ONBOARDING_STATUS(data))
            }
        } catch (error) {

        }
    }

    const handleAcceptTerms = async () => {
        try {
            setLoading(true)
            if (!acceptTerms) return
            const response = await agreeToTermsAndConditions(acceptTerms)

            if (response) {

                onSuccess?.()
                getCompanyOnboardingStatus()
            }

        } catch (error) {
            console.log("error", error)

        } finally {
            setLoading(false)
        }

    }

    return (
        <div className='py-8 px-4 mx-auto w-full h-full'>
            <div className="h-[80vh] overflow-y-auto bg-white dark:bg-darkBg">

                <TermsAndConditionsText />

                {!onboardingStatus?.companyInfo?.termsAndConditionsAccepted && !user.isInternalAdmin() &&
                    <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-5">
                        <CheckBoxInput
                            label="I agree to the terms and conditions."
                            name="completed"
                            checked={acceptTerms ?? false}
                            onChange={(data: boolean) => setAcceptTerms(data)}
                            disabled={onboardingStatus?.companyInfo?.termsAndConditionsAccepted || false}
                        />

                        <Button
                            label='Accept Terms '
                            onClick={handleAcceptTerms}
                            disabled={!acceptTerms || loading}
                            bgColor='primary'
                            className='w-full sm:w-auto'
                        />
                    </div>
                }
            </div>

            {onboardingStatus?.companyInfo?.termsAndConditionsAccepted && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 mx-4 mb-4 shadow-sm mt-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-green-800 mb-1">
                                    Terms & Conditions Accepted
                                </h3>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                    <div className="flex items-center gap-2">
                                        <BsPerson className="w-4 h-4 text-green-600" />
                                        <span className="text-green-700 ">
                                            By <span className="font-medium">{onboardingStatus?.companyInfo?.termsAndConditionsAcceptedBy}</span>
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <BsCalendar className="w-4 h-4 text-green-600" />
                                        <span className="text-sm text-green-600">
                                            On <span className="font-medium">{getFormattedDate({ date: onboardingStatus?.companyInfo?.termsAndConditionsAcceptedDateAndTime })}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex-shrink-0">
                            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                                <BadgeCheck className="w-4 h-4 mr-1" />
                                Accepted
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default TermsAndConditions


const TermsAndConditionsText = () => {
    return (
        <div className="px-6 py-4 text-sm leading-relaxed dark:text-darkBg-mainText">
            <div className="mb-8">
                <h1 className="text-2xl font-bold mb-4">BROLLY BROKER WORKSPACE - TERMS OF USE</h1>
                <p className="mb-4"><strong>Last Updated: December 2025</strong></p>
            </div>

            {/* 1. ACCEPTANCE AND SERVICES */}
            <section className="mb-6">
                <h2 className="text-lg font-semibold mb-3">1. Acceptance And Services</h2>

                <h3 className="text-md font-medium mb-2">1.1 Acceptance</h3>
                <p className="mb-3">{`These Terms of Use ("Terms") constitute a legally binding agreement between you ("User", "Broker", "you") and Brolly F & T Limited ("BROLLY", "Service Provider", "we", "us"), a company incorporated under the laws of Ghana.`}</p>
                <p className="mb-4">
                    By creating an account, accessing, or using the BROLLY Platform, you agree to be bound by these Terms.
                </p>

                <h3 className="text-md font-medium mb-2">1.2 Scope of License</h3>
                <p className="mb-3">
                    BROLLY grants you a limited, non-exclusive, non-transferable license to use the Brolly
                    Platform solely for legitimate insurance brokerage purposes, including but not limited to:
                </p>
                <ul className="list-disc pl-6 mb-4">
                    <li>a. Automated Policy Renewals</li>
                    <li>b. Remote Policy Creation</li>
                    <li>c. Centralized Data and Policy Management</li>
                    <li>d. Automated Commission Management</li>
                    <li>e. Premium Financing Integration</li>
                    <li>f. Claims Management</li>
                </ul>
            </section>

            {/* 2. ELIGIBILITY AND USE */}
            <section className="mb-6">
                <h2 className="text-lg font-semibold mb-3">2. Eligibility and Use</h2>
                <ul className="list-disc pl-6 mb-4">
                    <li className="mb-2">
                        You represent and warrant that you are duly licensed as an insurance broker or authorized representative in your jurisdiction and will comply with all applicable laws and regulations when using the Platform. You may not access or use the Platform if you are prohibited from doing so under applicable law.
                    </li>
                    <li className="mb-2">
                        You are responsible for all activities conducted under your account, including all users of your account. You agree to notify BROLLY immediately if you become aware of any unauthorized use of your account or login credentials.
                    </li>
                </ul>
            </section>

            {/* 3. FEES, BILLING, TRIAL */}
            <section className="mb-6">
                <h2 className="text-lg font-semibold mb-3">3. Fees, Billing, And Trial Conditions</h2>

                <h3 className="text-md font-medium mb-2">3.1 Free Trial Period</h3>
                <p className="mb-3">
                    The Broker receives a complimentary 30-day Free Trial for up to three (3) users. No monthly
                    subscription fees will be charged during this period.
                </p>

                <h3 className="text-md font-medium mb-2">3.2 Paid Subscription (Post-Trial)</h3>
                <p className="mb-3">
                    Upon expiration of the Free Trial, the Broker will automatically transition to the basic paid subscription plan unless actively cancelled.
                </p>
                <ul className="list-disc pl-6 mb-4">
                    <li>Base Monthly Fee: GHS 500.00, including licenses for up to five (5) users.</li>
                    <li>Extra User Fee: GHS 100.00 per user, per month, for any user exceeding the initial five.</li>
                </ul>

                <h3 className="text-md font-medium mb-2">3.4 Invoicing and Payment</h3>
                <p className="mb-3">
                    Fees are calculated automatically and shall be payable within seven (7) days of the monthly invoice date. Failure to pay may result in the suspension or termination of platform access.
                </p>
            </section>

            {/* 4. PREMIUM FINANCING */}
            <section className="mb-6">
                <h2 className="text-lg font-semibold mb-3">4. Premium Financing</h2>

                <h3 className="text-md font-medium mb-2">4.1 Availability and Terms</h3>
                <p className="mb-3">
                    The Premium Financing Add-On provides customers up to twelve (12) months to pay their premium. Interest on the outstanding financed balance is calculated at:
                </p>
                <ul className="list-disc pl-6 mb-4">
                    <li>3.5% per month (simple interest), if policy is underwritten by a Brolly partnered insurance company.</li>
                    <li>5% per month (simple interest), if policy is not underwritten by a Brolly partnered insurance company.</li>
                </ul>

                <h3 className="text-md font-medium mb-2">4.2 Risk of Service Suspension</h3>
                <p className="mb-3">
                    Brolly reserves the right to suspend access to the Premium Financing Add-On if the Broker consistently enrolls clients who default on their installment obligations.
                </p>

                <h3 className="text-md font-medium mb-2">4.3 Refund Priority</h3>
                <p className="mb-3">
                    Any unearned premium refund due from an underwriter shall be applied first to fully settle any outstanding loan balance owed to BROLLY&apos;s financing partner.
                </p>
            </section>

            {/* 5. DATA PROTECTION */}
            <section className="mb-6">
                <h2 className="text-lg font-semibold mb-3">5. Data Protection and Confidentiality</h2>

                <h3 className="text-md font-medium mb-2">5.1 Data Ownership and Use</h3>
                <p className="mb-3">
                    You retain full ownership of your customer data. BROLLY will process this data solely to provide and improve the services under this Agreement and for no other purpose.
                </p>

                <h3 className="text-md font-medium mb-2">5.2 Data Security and Sharing</h3>
                <p className="mb-3">
                    BROLLY implements appropriate security measures to protect the Broker’s data. BROLLY confirms that its data security protocols are covered by a recognized Data Protection Certificate (e.g., ISO 27001, GDPR, GDP Compliance Certification, or equivalent local standard).
                </p>
                <p className="mb-3">
                    Data will not be shared with any third party, except as strictly necessary to fulfill the services or as required by law.
                </p>

                <h3 className="text-md font-medium mb-2">5.3 Data Deletion</h3>
                <p className="mb-3">
                    Upon termination of the Broker’s account, BROLLY shall delete all associated customer data within ninety (90) days, unless legally required to retain it.
                </p>
            </section>

            {/* 6. IP & INDEMNITY */}
            <section className="mb-6">
                <h2 className="text-lg font-semibold mb-3">6. Intellectual Property and Indemnity</h2>

                <h3 className="text-md font-medium mb-2">6.1 Intellectual Property (IP)</h3>
                <p className="mb-3">
                    The BROLLY Platform, including all its software, code, algorithms, and underlying technology, is and shall remain the sole and exclusive Intellectual Property of BROLLY. The Broker acquires no ownership rights.
                </p>

                <h3 className="text-md font-medium mb-2">6.2 Broker Indemnity</h3>
                <p className="mb-3">
                    The Broker agrees to indemnify, defend, and hold harmless BROLLY from any claims, liabilities, losses, and legal expenses arising directly out of or relating to:
                </p>
                <ul className="list-disc pl-6 mb-4">
                    <li>a. The Broker’s breach of these Terms or regulatory obligations.</li>
                    <li>b. Any negligent act, fraudulent activity, or willful misconduct by the Broker.</li>
                    <li>c. Any third-party claim based on the Broker&apos;s actions or input of inaccurate data through the Platform.</li>
                </ul>
            </section>

            {/* 7. TERMINATION */}
            <section className="mb-6">
                <h2 className="text-lg font-semibold mb-3">7. Termination</h2>
                <p className="mb-3">
                    This Agreement remains in effect until the Broker cancels their subscription. The Broker may terminate the account at any time. BROLLY may terminate or suspend access immediately for any material breach, including non-payment or regulatory violations.
                </p>
                <p className="mb-3">
                    Any outstanding fees remain due and payable notwithstanding termination.
                </p>
            </section>

            {/* 8. FORCE MAJEURE */}
            <section className="mb-6">
                <h2 className="text-lg font-semibold mb-3">8. Force Majeure</h2>
                <p className="mb-3">
                    BROLLY shall not be liable for any failure or delay in performance resulting from causes beyond its reasonable control, including natural disasters, power outages, strikes, government actions, acts of terrorism, pandemics, or internet service provider failures.
                </p>
            </section>

            {/* 9. AMENDMENTS */}
            <section className="mb-6">
                <h2 className="text-lg font-semibold mb-3">9. Amendments</h2>
                <p className="mb-3">
                    BROLLY may update these Terms, including subscription plans, features, fees and billing, from time to time. Updates will be posted on the Platform, and continued use after changes take effect constitutes acceptance of the revised Terms.
                </p>
            </section>

            {/* 10. GOVERNING LAW */}
            <section className="mb-6">
                <h2 className="text-lg font-semibold mb-3">10. Governing Law and Dispute Resolution</h2>
                <p className="mb-3">
                    These Terms shall be governed by and construed in accordance with the laws of the Republic of Ghana. Any dispute shall first be resolved amicably. If unresolved within thirty (30) days, the dispute shall be submitted to arbitration in Accra under the rules of the Ghana Arbitration Centre, and the award shall be final and binding.
                </p>
            </section>

            {/* 11. ENTIRE AGREEMENT */}
            <section className="mb-6">
                <h2 className="text-lg font-semibold mb-3">11. Entire Agreement</h2>
                <p className="mb-3">
                    These Terms represent the entire agreement between you and BROLLY and supersede all prior agreements or understandings regarding use of the Platform.
                </p>
            </section>

            {/* 12. DIGITAL ACCEPTANCE */}
            <section className="mb-6">
                <h2 className="text-lg font-semibold mb-3">12. Digital Acceptance</h2>
                <p className="mb-3">{`
                    By checking the box, clicking "I Agree," or otherwise electronically accepting these Terms, you
                    acknowledge and agree that such action constitutes your electronic signature and has the same
                    force and effect as a physical signature.
                `}</p>
            </section>
        </div>
    );
};
